import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { Activity } from "lucide-react";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading, Empty } from "@/components/ds/PageShell";
import { SeverityDot, Confidence } from "@/components/ds/Severity";
import { Disclosure } from "@/components/ds/Disclosure";
import { toast } from "sonner";

const KIND_LABEL = {
    payroll_fortnightly: "Fortnightly payroll cadence",
    customer_late_payment: "Customer late-payment pattern",
    recurring_expense_creep: "Recurring expense creep",
    margin_compression: "Margin compression",
    supplier_concentration_growth: "Supplier concentration growth",
    seasonal_revenue_dip: "Seasonal revenue dip",
    bas_quarterly_pressure: "BAS quarterly pressure",
    cyclic_cash_dip: "Cyclic cash dip",
};

export default function Patterns() {
    const { selectedEntityId } = useEntity();
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const q = selectedEntityId === GROUP_VALUE ? "" : `?entity_id=${selectedEntityId}`;
        const { data } = await api.get(`/patterns${q}`);
        setItems(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntityId]);

    const rerun = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/patterns/detect");
            toast.success(`Detected ${data.total} pattern signatures`);
            await load();
        } finally {
            setBusy(false);
        }
    };

    const formatEvidence = (evidence) =>
        Object.entries(evidence || {})
            .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
            .join(" · ");

    return (
        <PageShell width="default" testId="patterns-page">
            <PageHeader
                eyebrow="Pattern engine"
                title="Patterns"
                subtitle="Recurring temporal and relational patterns across the business — payroll cadence, customer payment behaviour, margin trends, expense creep. Detected by rules, not by AI."
                action={
                    <button
                        type="button"
                        onClick={rerun}
                        disabled={busy}
                        className="inline-flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-md text-white disabled:opacity-50"
                        style={{ background: "var(--brand)" }}
                        data-testid="patterns-rerun"
                    >
                        <Activity size={13} strokeWidth={2} />
                        {busy ? "Detecting…" : "Re-run detection"}
                    </button>
                }
            />

            {loading ? (
                <Loading label="Loading patterns…" />
            ) : items.length === 0 ? (
                <Card variant="default" padding="lg">
                    <Empty
                        title="No patterns detected yet"
                        hint='Click "Re-run detection" to look across the latest data.'
                    />
                </Card>
            ) : (
                <Section
                    title={`${items.length} pattern${items.length === 1 ? "" : "s"} detected`}
                    subtitle="Each pattern carries its own confidence, frequency and evidence."
                >
                    <div className="space-y-2">
                        {items.map((p) => (
                            <Card
                                key={p.id}
                                variant="default"
                                padding="md"
                                testId={`pattern-${p.kind}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="pt-1.5">
                                        <SeverityDot level={p.severity} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                                            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">
                                                {KIND_LABEL[p.kind] || p.kind}
                                            </div>
                                            <span className="text-stone-300">·</span>
                                            <span className="text-[12px] text-stone-500">{p.entity_name}</span>
                                            <span className="text-stone-300">·</span>
                                            <Confidence level={p.confidence} />
                                        </div>
                                        <p className="text-[14px] text-stone-800 leading-snug">
                                            {p.business_impact}
                                        </p>
                                        <div className="text-[12px] text-stone-500 mt-1">
                                            Frequency: {p.frequency}
                                        </div>
                                        {p.evidence && Object.keys(p.evidence).length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-stone-100">
                                                <Disclosure
                                                    title="Evidence"
                                                    testId={`pattern-evidence-${p.kind}`}
                                                >
                                                    <div className="text-[12px] font-mono text-stone-600 break-all leading-relaxed">
                                                        {formatEvidence(p.evidence)}
                                                    </div>
                                                </Disclosure>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </Section>
            )}
        </PageShell>
    );
}
